'use client'

import { useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { Building2, Users, CheckCircle2, MapPin, Shield, TrendingUp, MessageCircle, Home, Briefcase, Award } from "lucide-react"
import { Button } from "@/components/ui/button"
import GetInTouchModal from "@/components/get-in-touch-modal"

export default function AboutPage() {
  const [showModal, setShowModal] = useState(false)
  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#d60500] via-[#b50400] to-[#8b0300] text-white py-20 md:py-28 mt-16 md:mt-20">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            {/* Trust Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full mb-6">
              <Award className="w-4 h-4" />
              <span className="text-sm font-semibold tracking-wider">PRC LICENSE NO. 0033251</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight font-heading">
              About Spire Group
            </h1>

            <p className="text-xl md:text-2xl font-light mb-4">
              Premier Luxury Real Estate Brokerage in Metro Manila
            </p>

            <p className="text-lg leading-relaxed mb-8 text-red-50">
              Specializing in <Link href="/properties?property_type=condo" className="underline hover:text-white">luxury condominiums</Link>, <Link href="/properties?property_type=penthouse" className="underline hover:text-white">premium penthouses</Link>, <Link href="/properties?property_type=house_and_lot" className="underline hover:text-white">exclusive estates</Link>, and <Link href="/properties?property_type=commercial_lot" className="underline hover:text-white">commercial properties</Link> across <Link href="/properties?city=Makati" className="underline hover:text-white">Makati</Link>, <Link href="/properties?city=BGC" className="underline hover:text-white">BGC</Link>, <Link href="/properties?city=Rockwell" className="underline hover:text-white">Rockwell</Link>, <Link href="/properties?city=Ortigas" className="underline hover:text-white">Ortigas</Link>, and CALABARZON.
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-1 h-8 bg-[#d60500]"></div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 font-heading">Who We Are</h2>
              </div>

              <div className="prose prose-lg text-gray-600 space-y-4">
                <p>
                  <strong className="text-gray-900">Spire Group is a premier real estate brokerage firm</strong> specializing in high-end residential and commercial properties across Metro Manila's most prestigious locations including <Link href="/properties?city=Forbes+Park" className="text-[#d60500] hover:underline">Forbes Park</Link>, <Link href="/properties?city=Ayala+Alabang" className="text-[#d60500] hover:underline">Ayala Alabang</Link>, <Link href="/properties?city=McKinley" className="text-[#d60500] hover:underline">McKinley Hill</Link>, and <Link href="/properties?city=Dasmarinas+Village" className="text-[#d60500] hover:underline">Dasmariñas Village</Link>.
                </p>

                <p>
                  We serve <strong className="text-gray-900">discerning buyers, sellers, investors, and renters</strong> seeking <Link href="/properties?listing_type=For+Sale&property_type=condo&min_price=50000000" className="text-[#d60500] hover:underline">luxury condominiums for sale</Link>, <Link href="/properties?listing_type=For+Rent&property_type=condo" className="text-[#d60500] hover:underline">premium condo units for rent</Link>, <Link href="/properties?property_type=house_and_lot&listing_type=For+Sale" className="text-[#d60500] hover:underline">exclusive houses and lots</Link>, and <Link href="/properties?property_type=office_space" className="text-[#d60500] hover:underline">prime commercial spaces</Link> — providing comprehensive services from property search and valuation to negotiation and transaction management.
                </p>

                <p className="text-xl font-semibold text-[#d60500] border-l-4 border-[#d60500] pl-4">
                  Excellence in service. Integrity in every transaction.
                </p>
              </div>
            </div>

            <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
              <Image
                src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab"
                alt="Luxury high-rise buildings in Makati CBD Manila"
                fill
                className="object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Expertise */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-1 h-8 bg-[#d60500]"></div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 font-heading">What We Do Best</h2>
            </div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Spire Group delivers professional real estate services with <strong className="text-gray-900">unmatched expertise and dedication</strong> across residential, commercial, and investment properties.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {[
              {
                icon: Building2,
                title: "Luxury Property Expertise",
                description: "Deep knowledge of premium condos, penthouses, and estates"
              },
              {
                icon: Users,
                title: "Client-Focused Service",
                description: "Personalized real estate solutions for every client"
              },
              {
                icon: CheckCircle2,
                title: "Due Diligence",
                description: "Thorough property verification and documentation"
              },
              {
                icon: Shield,
                title: "Licensed & Trusted",
                description: "PRC-licensed professional real estate service"
              }
            ].map((item, index) => (
              <div key={index} className="bg-white p-6 rounded-xl border border-gray-200 hover:border-[#d60500] hover:shadow-lg transition-all">
                <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-[#d60500]" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.description}</p>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-r from-[#d60500] to-[#b50400] text-white rounded-2xl p-8 text-center">
            <p className="text-2xl md:text-3xl font-bold mb-4 font-heading">Our Mission</p>
            <p className="text-xl font-light">
              To provide exceptional real estate brokerage services that exceed client expectations, facilitate successful property transactions, and build lasting relationships in Metro Manila's luxury property market.
            </p>
          </div>
        </div>
      </section>

      {/* Property Types */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-1 h-8 bg-[#d60500]"></div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 font-heading">Properties We Handle</h2>
            </div>
            <p className="text-lg text-gray-600">From luxury condominiums to exclusive estates and commercial properties</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { name: "Luxury Condominiums", icon: Building2, link: "/properties?property_type=condo" },
              { name: "Penthouses & Lofts", icon: Home, link: "/properties?property_type=penthouse" },
              { name: "Houses & Villas", icon: Home, link: "/properties?property_type=house_and_lot" },
              { name: "Office Spaces", icon: Briefcase, link: "/properties?property_type=office_space" },
              { name: "Commercial Properties", icon: Building2, link: "/properties?property_type=commercial_lot" },
              { name: "Investment Properties", icon: TrendingUp, link: "/properties?min_price=50000000" }
            ].map((type, index) => (
              <Link key={index} href={type.link} className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-200 hover:border-[#d60500] hover:bg-red-50 transition-all group">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-[#d60500] transition-colors">
                  <type.icon className="w-5 h-5 text-[#d60500] group-hover:text-white transition-colors" />
                </div>
                <span className="font-semibold text-gray-900 group-hover:text-[#d60500] transition-colors">{type.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Market Coverage */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-1 h-8 bg-[#d60500]"></div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 font-heading">Prime Locations We Serve</h2>
            </div>
            <p className="text-lg text-gray-600">Strategic coverage of Metro Manila's most prestigious neighborhoods and emerging growth areas</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {[
              { name: "Bonifacio Global City (BGC)", link: "/properties?city=BGC" },
              { name: "Makati Central Business District", link: "/properties?city=Makati" },
              { name: "Ortigas Center", link: "/properties?city=Ortigas" },
              { name: "Rockwell Center", link: "/properties?city=Rockwell" },
              { name: "Quezon City", link: "/properties?city=Quezon" },
              { name: "Manila Bay Area", link: "/properties?city=Manila" },
              { name: "Alabang & South Metro Manila", link: "/properties?city=Muntinlupa" },
              { name: "Forbes Park & Makati Villages", link: "/properties?city=Forbes+Park" },
              { name: "McKinley Hill & West", link: "/properties?city=McKinley" },
              { name: "CALABARZON (Laguna, Cavite)", link: "/properties" },
            ].map((area, index) => (
              <Link key={index} href={area.link} className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-200 shadow-sm hover:border-[#d60500] hover:shadow-lg transition-all group">
                <MapPin className="w-5 h-5 text-[#d60500] flex-shrink-0 group-hover:scale-110 transition-transform" />
                <span className="font-semibold text-gray-900 group-hover:text-[#d60500] transition-colors">{area.name}</span>
              </Link>
            ))}
          </div>

          <p className="text-center text-gray-600 italic">
            Strategic presence in <strong className="text-gray-900">high-value markets and emerging growth areas</strong> with exclusive access to <Link href="/properties?listing_type=For+Sale" className="text-[#d60500] hover:underline">premium properties for sale</Link> and <Link href="/properties?listing_type=For+Rent" className="text-[#d60500] hover:underline">luxury rentals</Link>.
          </p>
        </div>
      </section>

      {/* For Buyers */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl order-2 md:order-1">
              <Image
                src="https://images.unsplash.com/photo-1560518883-ce09059eeffa"
                alt="Luxury residential property in BGC Manila"
                fill
                className="object-cover"
              />
            </div>

            <div className="order-1 md:order-2">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-1 h-8 bg-[#d60500]"></div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 font-heading">For Buyers & Renters</h2>
              </div>

              <p className="text-xl font-semibold text-gray-900 mb-6">Find Your Perfect Luxury Property in Metro Manila</p>

              <ul className="space-y-4">
                {[
                  "Curated property selection based on your requirements and budget",
                  "Expert market insights and pricing guidance for BGC, Makati, Ortigas",
                  "Professional viewing coordination for condos, penthouses, and estates",
                  "Comprehensive due diligence support and title verification",
                  "Negotiation and transaction management from offer to closing"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-[#d60500] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>

              <p className="mt-6 text-gray-600">
                Whether <Link href="/properties?listing_type=For+Sale" className="text-[#d60500] hover:underline font-semibold">buying a luxury condo</Link> or <Link href="/properties?listing_type=For+Rent" className="text-[#d60500] hover:underline font-semibold">renting a premium penthouse</Link>, we ensure a <strong className="text-gray-900">smooth and informed decision-making process</strong>.
              </p>

              <div className="mt-6">
                <Link href="/properties" className="inline-flex items-center gap-2 text-[#d60500] hover:text-[#b50400] font-semibold">
                  Browse All Properties →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Sellers */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-1 h-8 bg-[#d60500]"></div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 font-heading">For Sellers & Landlords</h2>
              </div>

              <p className="text-xl font-semibold text-gray-900 mb-6">Maximize Your Property Value in Metro Manila's Luxury Market</p>

              <ul className="space-y-4">
                {[
                  "Professional property valuation and competitive pricing strategy",
                  "Targeted marketing to qualified buyers and high-net-worth investors",
                  "Premium listing presentation with professional photography",
                  "Qualified buyer screening and financial verification",
                  "Expert negotiation for best terms and maximum value"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <TrendingUp className="w-6 h-6 text-[#d60500] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>

              <p className="mt-6 text-gray-600">
                We deliver <strong className="text-gray-900">results-driven brokerage service</strong> to help you sell your <Link href="/properties?property_type=condo" className="text-[#d60500] hover:underline">luxury condominium</Link>, <Link href="/properties?property_type=house_and_lot" className="text-[#d60500] hover:underline">exclusive estate</Link>, or <Link href="/properties?property_type=commercial_lot" className="text-[#d60500] hover:underline">commercial property</Link> efficiently and profitably.
              </p>

              <div className="mt-6">
                <Link href="/list-property" className="inline-flex items-center gap-2 text-[#d60500] hover:text-[#b50400] font-semibold">
                  List Your Property with Us →
                </Link>
              </div>
            </div>

            <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
              <Image
                src="https://images.unsplash.com/photo-1560520653-9e0e4c89eb11"
                alt="Premium property marketing in Metro Manila"
                fill
                className="object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Spire */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-1 h-8 bg-[#d60500]"></div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 font-heading">Why Choose Spire Group for Your Real Estate Needs</h2>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              "PRC-licensed professional real estate service",
              "Extensive luxury property network in Metro Manila",
              "Deep market expertise and investment insights",
              "Personalized client approach and dedicated support",
              "Transparent processes and ethical practices",
              "Proven track record in high-value transactions"
            ].map((reason, index) => (
              <div key={index} className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200 shadow-sm hover:border-[#d60500] transition-all">
                <CheckCircle2 className="w-5 h-5 text-[#d60500] flex-shrink-0" />
                <span className="font-semibold text-gray-900">{reason}</span>
              </div>
            ))}
          </div>

          <p className="text-center text-xl text-gray-600 mt-8">
            Experience <strong className="text-gray-900">professionalism, expertise, and results</strong> with every property transaction in BGC, Makati, Rockwell, Ortigas, and beyond.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-[#d60500] to-[#b50400]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <MessageCircle className="w-16 h-16 text-white mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-heading">
            Ready to Work With Us?
          </h2>
          <p className="text-xl text-red-50 mb-8 font-light">
            Whether buying, selling, renting, or investing in Metro Manila's luxury real estate market — let's discuss how we can help you achieve your property goals.
          </p>
          <Button
            onClick={() => setShowModal(true)}
            size="lg"
            className="bg-white text-[#d60500] hover:bg-gray-100 text-lg px-8 py-6 rounded-none font-semibold shadow-xl"
          >
            <MessageCircle className="w-5 h-5 mr-2" />
            Get in Touch
          </Button>
          <p className="text-red-100 mt-6 text-sm">
            Speak with our team of licensed real estate professionals.
          </p>
        </div>
      </section>

      <GetInTouchModal isOpen={showModal} onClose={() => setShowModal(false)} source="about-page" />
    </div>
  )
}
